import { useCallback, useEffect, useMemo, useState } from 'react';
import { auditService } from '../../services/auditService';
import { userService } from '../../services/userService';
import { teamService } from '../../services/teamService';
import { getRoleLabel } from '../../utils/permissions';
import { toast } from '../../utils/toast';
import Pager, { usePaged } from '../Pager';
import { PageLoader } from '../Loader';
import './Manpower.css';

// ---- Adjustments (same rules as the schedule's member overrides) -----------------

const ACTIONS = [
  { value: 'none', label: 'Working the whole schedule' },
  { value: 'exclude_range', label: 'Off on some days' },
  { value: 'replace_full', label: 'Replaced for the whole schedule' },
  { value: 'replace_range', label: 'Replaced on some days' },
  { value: 'exclude_full', label: 'Removed from this schedule' },
];
const NO_CHANGE = { action: 'none', from: '', to: '', replacement: '', notes: '' };
const needsDays = (a) => a === 'exclude_range' || a === 'replace_range';
const needsReplacement = (a) => a === 'replace_full' || a === 'replace_range';

/** YYYY-MM-DD in this browser's time zone (dates may arrive as ISO timestamps). */
function toDay(value) {
  if (!value) return '';
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const d = new Date(text);
  if (Number.isNaN(d.getTime())) return text.slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fromOverride(o) {
  if (!o) return { ...NO_CHANGE };
  const range = Boolean(o.effective_from || o.effective_to);
  const replace = o.override_type === 'replace';
  return {
    action: replace ? (range ? 'replace_range' : 'replace_full') : range ? 'exclude_range' : 'exclude_full',
    from: toDay(o.effective_from),
    to: toDay(o.effective_to),
    replacement: o.replacement_user_id ? String(o.replacement_user_id) : '',
    notes: o.notes || '',
  };
}

function toOverride(teamId, userId, s) {
  if (!s || s.action === 'none') return null;
  return {
    team_id: Number(teamId),
    user_id: Number(userId),
    override_type: needsReplacement(s.action) ? 'replace' : 'exclude',
    replacement_user_id: needsReplacement(s.action) ? Number(s.replacement) || null : null,
    effective_from: needsDays(s.action) ? s.from || null : null,
    effective_to: needsDays(s.action) ? s.to || null : null,
    notes: s.notes?.trim() || null,
  };
}

// ---- Small helpers ----------------------------------------------------------------

const ROLE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'member', label: 'Scanning Members' },
  { id: 'asa', label: 'Data ASA' },
  { id: 'admin', label: 'Admins' },
];
const roleGroup = (u) => (!u ? 'member' : u.role_name === 'auditor' ? (u.is_manager ? 'asa' : 'member') : 'admin');
const ROLE_ORDER = { asa: 0, member: 1, admin: 2 };
const nameOf = (u) => u?.name || u?.email || 'Unknown user';

function shortDay(value) {
  if (!value) return '';
  const d = new Date(`${toDay(value)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function clashText(list) {
  if (!list?.length) return '';
  const first = list[0];
  const more = list.length > 1 ? ` +${list.length - 1} more` : '';
  return `Also on ${first.audit_name} (${shortDay(first.start_at)} – ${shortDay(first.end_at)})${more}`;
}

function statusText(s, usersById) {
  if (!s || s.action === 'none') return '';
  const who = nameOf(usersById.get(String(s.replacement)));
  const days = s.from || s.to ? `${shortDay(s.from) || 'start'} – ${shortDay(s.to) || 'end'}` : '';
  switch (s.action) {
    case 'exclude_full':
      return 'Removed from this schedule';
    case 'exclude_range':
      return `Off ${days || '(choose days)'}`;
    case 'replace_full':
      return s.replacement ? `Replaced by ${who}` : 'Replaced (choose who)';
    case 'replace_range':
      return s.replacement ? `${who} covers ${days || '(choose days)'}` : 'Replaced on some days (choose who)';
    default:
      return '';
  }
}

/** Compares two drafts regardless of the order the adjustments were made in. */
const canonical = (d) =>
  JSON.stringify({
    ...d,
    overrides: Object.keys(d.overrides)
      .sort()
      .map((k) => [k, d.overrides[k]]),
  });
const sameDraft = (a, b) => canonical(a) === canonical(b);

/**
 * Manpower for one schedule: who scans it.
 * Left: people and teams to add (search, role, free / busy on these dates).
 * Right: everyone on the schedule, grouped by role, with the lead auditor,
 * clashes and per-person adjustments (off some days, replaced, removed).
 *
 * People come from shared teams (whole team added) or are added one by one
 * (kept in the schedule's own team). A team member is "removed" with an
 * adjustment, so the shared team itself is not changed.
 *
 * Props: auditId, layout ('page' | 'modal'), onSaved(audit), onDirtyChange(bool)
 */
function ManpowerEditor({ auditId, layout = 'page', onSaved, onDirtyChange }) {
  const [audit, setAudit] = useState(null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(new Map()); // user id -> other schedules on these dates
  const [initial, setInitial] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dynamicTeam, setDynamicTeam] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [tab, setTab] = useState('people');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [freeOnly, setFreeOnly] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [teamSearch, setTeamSearch] = useState('');
  const [rosterSearch, setRosterSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [auditData, roster, userList, teamList] = await Promise.all([
        auditService.getById(auditId),
        auditService.getRoster(auditId),
        userService.getAll(),
        teamService.getAll(),
      ]);
      const people = (userList || []).filter((u) => ['auditor', 'admin', 'super_admin'].includes(u.role_name));
      const dyn = (roster.teams || []).find((t) => t.is_dynamic) || null;
      const overrides = {};
      (roster.overrides || []).forEach((o) => {
        const key = dyn && String(o.team_id) === String(dyn.id) ? `dyn:${o.user_id}` : `${o.team_id}:${o.user_id}`;
        overrides[key] = fromOverride(o);
      });
      const next = {
        teamIds: (roster.teams || []).filter((t) => !t.is_dynamic).map((t) => String(t.id)).sort(),
        extraIds: dyn ? (dyn.members || []).map((m) => String(m.user_id ?? m.id)).sort() : [],
        lead: auditData.lead_auditor_id ? String(auditData.lead_auditor_id) : '',
        overrides,
      };
      setAudit(auditData);
      setUsers(people);
      setTeams((teamList || []).filter((t) => !t.is_dynamic));
      setDynamicTeam(dyn);
      setInitial(next);
      setDraft(next);
      setSelected(new Set());
      setEditing(null);
      setError('');
      // Who is busy on another schedule in this one's dates.
      if (people.length && auditData.start_at && auditData.end_at) {
        auditService
          .previewConflicts({
            start_at: auditData.start_at,
            end_at: auditData.end_at,
            team_ids: [],
            dynamic_member_ids: people.map((u) => u.id),
            exclude_audit_id: auditData.id,
          })
          .then((rows) => setBusy(new Map((rows || []).map((r) => [String(r.user_id), r.conflicting_audits || []]))))
          .catch(() => setBusy(new Map()));
      }
    } catch (err) {
      setError(err.message);
    }
  }, [auditId]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = Boolean(draft && initial && !sameDraft(draft, initial));
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);

  const usersById = useMemo(() => new Map(users.map((u) => [String(u.id), u])), [users]);
  const teamsById = useMemo(() => new Map(teams.map((t) => [String(t.id), t])), [teams]);
  const closed = audit?.end_at ? new Date(audit.end_at) < new Date() : false;
  const startDay = toDay(audit?.start_at);
  const endDay = toDay(audit?.end_at);

  // Everyone on the schedule: one row per (team, person); people added one by one use "dyn".
  const rows = useMemo(() => {
    if (!draft) return [];
    const list = [];
    draft.teamIds.forEach((teamId) => {
      const team = teamsById.get(teamId);
      (team?.members || []).forEach((m) => {
        const uid = String(m.id);
        list.push({ key: `${teamId}:${uid}`, teamId, uid, source: team.name, user: usersById.get(uid) || m });
      });
    });
    draft.extraIds.forEach((uid) => {
      list.push({ key: `dyn:${uid}`, teamId: 'dyn', uid, source: 'Added individually', user: usersById.get(uid) || { id: uid } });
    });
    return list.map((row) => ({ ...row, adj: draft.overrides[row.key] || NO_CHANGE, clash: busy.get(row.uid) || [] }));
  }, [draft, teamsById, usersById, busy]);

  const onSchedule = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (r.adj.action === 'exclude_full') return;
      map.set(r.uid, [...(map.get(r.uid) || []), r.source]);
    });
    return map;
  }, [rows]);

  const counts = useMemo(() => {
    const c = { total: onSchedule.size, member: 0, asa: 0, admin: 0, clash: 0, adjusted: 0 };
    onSchedule.forEach((_, uid) => {
      c[roleGroup(usersById.get(uid))] += 1;
      if (busy.has(uid)) c.clash += 1;
    });
    c.adjusted = rows.filter((r) => r.adj.action !== 'none' && r.adj.action !== 'exclude_full').length;
    return c;
  }, [onSchedule, usersById, busy, rows]);

  // ---- Changes -------------------------------------------------------------------

  const update = (fn) => setDraft((prev) => fn(prev));
  const setAdj = (key, patch) =>
    update((d) => {
      const next = { ...(d.overrides[key] || NO_CHANGE), ...patch };
      const overrides = { ...d.overrides };
      if (next.action === 'none' && !next.notes) delete overrides[key];
      else overrides[key] = next;
      return { ...d, overrides };
    });

  const addPeople = (ids) => {
    const add = ids.map(String).filter((id) => !draft.extraIds.includes(id));
    // Someone removed from a team row comes back on that row instead.
    const restoreKeys = rows.filter((r) => ids.map(String).includes(r.uid) && r.adj.action === 'exclude_full').map((r) => r.key);
    const fresh = add.filter((id) => !rows.some((r) => r.uid === id && r.teamId !== 'dyn'));
    update((d) => {
      const overrides = { ...d.overrides };
      restoreKeys.forEach((k) => delete overrides[k]);
      return { ...d, overrides, extraIds: [...new Set([...d.extraIds, ...fresh])].sort() };
    });
    setPicked(new Set());
    const n = fresh.length + restoreKeys.length;
    if (n) toast.success(`${n} ${n === 1 ? 'person' : 'people'} added`, 'Save to keep the change.', { duration: 3000 });
  };

  const removeRows = (keys) => {
    const list = rows.filter((r) => keys.includes(r.key));
    update((d) => {
      const overrides = { ...d.overrides };
      let extraIds = d.extraIds;
      list.forEach((r) => {
        if (r.teamId === 'dyn') {
          extraIds = extraIds.filter((id) => id !== r.uid);
          delete overrides[r.key];
        } else {
          overrides[r.key] = { ...NO_CHANGE, action: 'exclude_full', notes: overrides[r.key]?.notes || '' };
        }
      });
      const lead = list.some((r) => r.uid === d.lead) && !rows.some((r) => r.uid === d.lead && !keys.includes(r.key)) ? '' : d.lead;
      return { ...d, overrides, extraIds, lead };
    });
    setSelected(new Set());
    if (editing && keys.includes(editing)) setEditing(null);
  };

  const restoreRows = (keys) => {
    update((d) => {
      const overrides = { ...d.overrides };
      keys.forEach((k) => {
        if (overrides[k]?.action === 'exclude_full') delete overrides[k];
      });
      return { ...d, overrides };
    });
    setSelected(new Set());
  };

  const toggleTeam = (teamId) => {
    const id = String(teamId);
    update((d) => {
      if (d.teamIds.includes(id)) {
        const overrides = Object.fromEntries(Object.entries(d.overrides).filter(([k]) => !k.startsWith(`${id}:`)));
        return { ...d, teamIds: d.teamIds.filter((t) => t !== id), overrides };
      }
      return { ...d, teamIds: [...d.teamIds, id].sort() };
    });
  };

  // ---- Save --------------------------------------------------------------------------

  const problems = rows.filter(
    (r) =>
      (needsReplacement(r.adj.action) && !r.adj.replacement) ||
      (needsDays(r.adj.action) && !r.adj.from && !r.adj.to) ||
      (r.adj.from && r.adj.to && r.adj.to < r.adj.from),
  );

  const save = async () => {
    if (problems.length) {
      toast.error('Some adjustments are not complete', `${nameOf(problems[0].user)}: choose the days and / or who replaces them.`);
      setEditing(problems[0].key);
      return;
    }
    setSaving(true);
    try {
      const payload = { team_ids: draft.teamIds.map(Number), lead_auditor_id: draft.lead ? Number(draft.lead) : null };
      if (draft.extraIds.length) {
        payload.dynamic_team = {
          name: dynamicTeam?.name || `${audit.name} — individual members`.slice(0, 250),
          member_ids: draft.extraIds.map(Number),
        };
      }
      const saved = await auditService.update(auditId, payload);
      // The schedule's own team may be new: read its id before saving adjustments.
      const roster = await auditService.getRoster(auditId);
      const dyn = (roster.teams || []).find((t) => t.is_dynamic);
      const overrides = rows
        .map((r) => toOverride(r.teamId === 'dyn' ? dyn?.id : r.teamId, r.uid, r.adj))
        .filter((o) => o && o.team_id);
      await auditService.updateMemberOverrides(auditId, overrides);
      toast.success('Team saved', `${counts.total} ${counts.total === 1 ? 'person' : 'people'} on ${audit.name}`);
      await load();
      onSaved?.(saved);
    } catch (err) {
      toast.error('Team not saved', err.message);
    } finally {
      setSaving(false);
    }
  };

  // ---- Lists ---------------------------------------------------------------------------

  const people = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users
      .filter((u) => roleFilter === 'all' || roleGroup(u) === roleFilter)
      .filter((u) => !freeOnly || !busy.has(String(u.id)))
      .filter((u) => !term || `${u.name || ''} ${u.email || ''} ${getRoleLabel(u)}`.toLowerCase().includes(term))
      .sort(
        (a, b) =>
          Number(onSchedule.has(String(a.id))) - Number(onSchedule.has(String(b.id))) ||
          Number(busy.has(String(a.id))) - Number(busy.has(String(b.id))) ||
          ROLE_ORDER[roleGroup(a)] - ROLE_ORDER[roleGroup(b)] ||
          nameOf(a).localeCompare(nameOf(b)),
      );
  }, [users, roleFilter, freeOnly, busy, search, onSchedule]);
  const peoplePaged = usePaged(people, { pageSize: 25, sizes: [25, 50, 100], resetKey: `${search}|${roleFilter}|${freeOnly}`, storageKey: 'manpower-people' });

  const teamList = useMemo(() => {
    const term = teamSearch.trim().toLowerCase();
    return teams
      .filter((t) => !term || `${t.name} ${(t.members || []).map((m) => m.name).join(' ')}`.toLowerCase().includes(term))
      .sort((a, b) => Number(draft?.teamIds.includes(String(b.id))) - Number(draft?.teamIds.includes(String(a.id))) || a.name.localeCompare(b.name));
  }, [teams, teamSearch, draft]);
  const teamsPaged = usePaged(teamList, { pageSize: 15, sizes: [15, 30, 60], resetKey: teamSearch, storageKey: 'manpower-teams' });

  const rosterShown = useMemo(() => {
    const term = rosterSearch.trim().toLowerCase();
    return rows
      .filter((r) => !term || `${nameOf(r.user)} ${r.user?.email || ''} ${r.source}`.toLowerCase().includes(term))
      .sort(
        (a, b) =>
          Number(a.adj.action === 'exclude_full') - Number(b.adj.action === 'exclude_full') ||
          ROLE_ORDER[roleGroup(a.user)] - ROLE_ORDER[roleGroup(b.user)] ||
          nameOf(a.user).localeCompare(nameOf(b.user)),
      );
  }, [rows, rosterSearch]);
  const rosterPaged = usePaged(rosterShown, { pageSize: 25, sizes: [25, 50, 100], resetKey: rosterSearch, storageKey: 'manpower-roster' });

  const leadOptions = useMemo(() => {
    const ids = [...onSchedule.keys()];
    if (draft?.lead && !ids.includes(draft.lead)) ids.push(draft.lead);
    return ids
      .map((id) => usersById.get(id))
      .filter(Boolean)
      .sort((a, b) => ROLE_ORDER[roleGroup(a)] - ROLE_ORDER[roleGroup(b)] || nameOf(a).localeCompare(nameOf(b)));
  }, [onSchedule, draft, usersById]);

  if (error && !draft) return <p className="mp-error">{error}</p>;
  if (!draft || !audit) return <PageLoader label="Loading the team…" />;

  const readOnly = closed;
  const selectedRows = rows.filter((r) => selected.has(r.key));
  const groupHeads = new Set();

  return (
    <div className={`mp mp--${layout}`}>
      {/* Summary strip */}
      <div className="mp__summary">
        <div className="mp__stats">
          <span className="mp-stat mp-stat--total">
            <b>{counts.total}</b> on this schedule
          </span>
          <span className="mp-stat">
            <b>{counts.member}</b> Scanning Members
          </span>
          <span className="mp-stat">
            <b>{counts.asa}</b> Data ASA
          </span>
          {counts.admin ? (
            <span className="mp-stat">
              <b>{counts.admin}</b> Admins
            </span>
          ) : null}
          <span className={`mp-stat${counts.clash ? ' mp-stat--warn' : ''}`}>
            <b>{counts.clash}</b> clash{counts.clash === 1 ? '' : 'es'}
          </span>
          {counts.adjusted ? (
            <span className="mp-stat mp-stat--info">
              <b>{counts.adjusted}</b> adjusted
            </span>
          ) : null}
        </div>
        <label className="mp__lead">
          <span>Lead auditor</span>
          <select value={draft.lead} onChange={(e) => update((d) => ({ ...d, lead: e.target.value }))} disabled={readOnly}>
            <option value="">Not set</option>
            {leadOptions.map((u) => (
              <option key={u.id} value={String(u.id)}>
                {nameOf(u)} ({getRoleLabel(u)})
              </option>
            ))}
          </select>
        </label>
      </div>
      {readOnly ? <p className="mp-note">This schedule has ended, so its team can be viewed but not changed.</p> : null}

      <div className="mp__panes">
        {/* ---- Left: add people / teams ---- */}
        {!readOnly ? (
          <section className="mp-pane mp-pane--add" aria-label="Add people">
            <div className="mp-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={tab === 'people'} onClick={() => setTab('people')}>
                People <span>{users.length}</span>
              </button>
              <button type="button" role="tab" aria-selected={tab === 'teams'} onClick={() => setTab('teams')}>
                Teams <span>{teams.length}</span>
              </button>
            </div>

            {tab === 'people' ? (
              <>
                <div className="mp-tools">
                  <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email…" aria-label="Search people" />
                  <div className="mp-chips" role="group" aria-label="Role">
                    {ROLE_FILTERS.map((f) => (
                      <button key={f.id} type="button" aria-pressed={roleFilter === f.id} onClick={() => setRoleFilter(f.id)}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <label className="mp-toggle">
                    <input type="checkbox" checked={freeOnly} onChange={(e) => setFreeOnly(e.target.checked)} />
                    Free on these dates only
                  </label>
                </div>
                <ul className="mp-list" ref={peoplePaged.anchorRef}>
                  {peoplePaged.items.map((u) => {
                    const id = String(u.id);
                    const on = onSchedule.get(id);
                    const clash = busy.get(id);
                    return (
                      <li key={id} className={`mp-person${on ? ' is-on' : ''}`}>
                        <label className="mp-person__pick">
                          <input
                            type="checkbox"
                            disabled={Boolean(on)}
                            checked={picked.has(id)}
                            onChange={(e) =>
                              setPicked((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(id);
                                else next.delete(id);
                                return next;
                              })
                            }
                            aria-label={`Pick ${nameOf(u)}`}
                          />
                        </label>
                        <div className="mp-person__who">
                          <strong>{nameOf(u)}</strong>
                          <span>
                            <em className={`mp-role mp-role--${roleGroup(u)}`}>{getRoleLabel(u)}</em> {u.email}
                          </span>
                          {clash ? <small className="mp-clash">{clashText(clash)}</small> : null}
                        </div>
                        {on ? (
                          <span className="mp-pill mp-pill--on" title={on.join(', ')}>
                            On schedule
                          </span>
                        ) : (
                          <button type="button" className="mp-btn mp-btn--add" onClick={() => addPeople([id])}>
                            + Add
                          </button>
                        )}
                      </li>
                    );
                  })}
                  {!people.length ? <li className="mp-empty">No one matches.</li> : null}
                </ul>
                <Pager paged={peoplePaged} noun="people" compact />
                {picked.size ? (
                  <div className="mp-pickbar">
                    <span>{picked.size} picked</span>
                    <button type="button" className="mp-link" onClick={() => setPicked(new Set())}>
                      Clear
                    </button>
                    <button type="button" className="mp-btn mp-btn--primary" onClick={() => addPeople([...picked])}>
                      Add {picked.size} to schedule
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="mp-tools">
                  <input type="search" value={teamSearch} onChange={(e) => setTeamSearch(e.target.value)} placeholder="Search team or member…" aria-label="Search teams" />
                </div>
                <ul className="mp-list" ref={teamsPaged.anchorRef}>
                  {teamsPaged.items.map((t) => {
                    const id = String(t.id);
                    const on = draft.teamIds.includes(id);
                    const members = t.members || [];
                    const clashCount = members.filter((m) => busy.has(String(m.id))).length;
                    return (
                      <li key={id} className={`mp-team${on ? ' is-on' : ''}`}>
                        <div className="mp-person__who">
                          <strong>{t.name}</strong>
                          <span>
                            {members.length} member{members.length === 1 ? '' : 's'}
                            {clashCount ? <em className="mp-clash-inline"> · {clashCount} busy on these dates</em> : null}
                          </span>
                          <small>{members.slice(0, 5).map((m) => m.name || m.email).join(', ')}{members.length > 5 ? ` +${members.length - 5}` : ''}</small>
                        </div>
                        <button type="button" className={`mp-btn ${on ? 'mp-btn--ghost' : 'mp-btn--add'}`} onClick={() => toggleTeam(id)}>
                          {on ? 'Remove team' : '+ Add team'}
                        </button>
                      </li>
                    );
                  })}
                  {!teamList.length ? <li className="mp-empty">No teams match.</li> : null}
                </ul>
                <Pager paged={teamsPaged} noun="teams" compact />
              </>
            )}
          </section>
        ) : null}

        {/* ---- Right: on this schedule ---- */}
        <section className="mp-pane mp-pane--roster" aria-label="On this schedule">
          <header className="mp-roster__head">
            <div>
              <h3>On this schedule</h3>
              <div className="mp-teamchips">
                {draft.teamIds.map((id) => (
                  <span key={id} className="mp-teamchip">
                    {teamsById.get(id)?.name || `Team ${id}`}
                    {!readOnly ? (
                      <button type="button" onClick={() => toggleTeam(id)} aria-label={`Remove team ${teamsById.get(id)?.name || id}`}>
                        ×
                      </button>
                    ) : null}
                  </span>
                ))}
                {draft.extraIds.length ? <span className="mp-teamchip mp-teamchip--dyn">{draft.extraIds.length} added individually</span> : null}
                {!draft.teamIds.length && !draft.extraIds.length ? <span className="mp-muted">Nobody yet — add people or a team.</span> : null}
              </div>
            </div>
            <input type="search" value={rosterSearch} onChange={(e) => setRosterSearch(e.target.value)} placeholder="Search on this schedule…" aria-label="Search the schedule's team" />
          </header>

          {selectedRows.length && !readOnly ? (
            <div className="mp-bulk">
              <span>{selectedRows.length} selected</span>
              <button type="button" className="mp-btn mp-btn--ghost" onClick={() => removeRows(selectedRows.map((r) => r.key))}>
                Remove from schedule
              </button>
              {selectedRows.some((r) => r.adj.action === 'exclude_full') ? (
                <button type="button" className="mp-btn mp-btn--ghost" onClick={() => restoreRows(selectedRows.map((r) => r.key))}>
                  Put back
                </button>
              ) : null}
              <button type="button" className="mp-link" onClick={() => setSelected(new Set())}>
                Clear
              </button>
            </div>
          ) : null}

          <div className="mp-table" ref={rosterPaged.anchorRef}>
            <table>
              <thead>
                <tr>
                  <th className="mp-col-check">
                    {!readOnly ? (
                      <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        checked={rosterPaged.items.length > 0 && rosterPaged.items.every((r) => selected.has(r.key))}
                        onChange={(e) =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            rosterPaged.items.forEach((r) => (e.target.checked ? next.add(r.key) : next.delete(r.key)));
                            return next;
                          })
                        }
                      />
                    ) : null}
                  </th>
                  <th>Person</th>
                  <th>From</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rosterPaged.items.map((r) => {
                  const removed = r.adj.action === 'exclude_full';
                  const group = removed ? 'removed' : roleGroup(r.user);
                  const showHead = !groupHeads.has(group);
                  groupHeads.add(group);
                  const isLead = draft.lead === r.uid;
                  const open = editing === r.key;
                  const status = statusText(r.adj, usersById);
                  const incomplete = problems.some((p) => p.key === r.key);
                  return [
                    showHead ? (
                      <tr key={`h-${group}`} className="mp-grouprow">
                        <td colSpan={5}>
                          {group === 'removed'
                            ? 'Removed from this schedule'
                            : group === 'asa'
                              ? 'Data ASA'
                              : group === 'member'
                                ? 'Scanning Members'
                                : 'Admins'}
                        </td>
                      </tr>
                    ) : null,
                    <tr key={r.key} className={`${removed ? 'is-removed' : ''}${open ? ' is-open' : ''}`}>
                      <td className="mp-col-check">
                        {!readOnly ? (
                          <input
                            type="checkbox"
                            checked={selected.has(r.key)}
                            onChange={(e) =>
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(r.key);
                                else next.delete(r.key);
                                return next;
                              })
                            }
                            aria-label={`Select ${nameOf(r.user)}`}
                          />
                        ) : null}
                      </td>
                      <td>
                        <div className="mp-who">
                          <strong>
                            {nameOf(r.user)}
                            {isLead ? <span className="mp-lead">Lead</span> : null}
                          </strong>
                          <span>
                            <em className={`mp-role mp-role--${roleGroup(r.user)}`}>{getRoleLabel(r.user)}</em> {r.user?.email}
                          </span>
                        </div>
                      </td>
                      <td className="mp-from">{r.source}</td>
                      <td>
                        {status ? <span className={`mp-pill ${removed ? 'mp-pill--removed' : incomplete ? 'mp-pill--bad' : 'mp-pill--info'}`}>{status}</span> : <span className="mp-pill mp-pill--ok">Working</span>}
                        {r.clash.length && !removed ? <small className="mp-clash">{clashText(r.clash)}</small> : null}
                      </td>
                      <td className="mp-actions">
                        {readOnly ? null : removed ? (
                          <button type="button" className="mp-link" onClick={() => restoreRows([r.key])}>
                            Put back
                          </button>
                        ) : (
                          <>
                            <button type="button" className="mp-link" onClick={() => setEditing(open ? null : r.key)} aria-expanded={open}>
                              {open ? 'Done' : 'Adjust'}
                            </button>
                            <button type="button" className="mp-link mp-link--danger" onClick={() => removeRows([r.key])}>
                              Remove
                            </button>
                          </>
                        )}
                      </td>
                    </tr>,
                    open ? (
                      <tr key={`e-${r.key}`} className="mp-editrow">
                        <td />
                        <td colSpan={4}>
                          <div className="mp-edit">
                            <label>
                              Change
                              <select value={r.adj.action} onChange={(e) => setAdj(r.key, { action: e.target.value })}>
                                {ACTIONS.map((a) => (
                                  <option key={a.value} value={a.value}>
                                    {a.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                            {needsDays(r.adj.action) ? (
                              <>
                                <label>
                                  From
                                  <input type="date" value={r.adj.from} min={startDay} max={endDay} onChange={(e) => setAdj(r.key, { from: e.target.value })} />
                                </label>
                                <label>
                                  To
                                  <input type="date" value={r.adj.to} min={r.adj.from || startDay} max={endDay} onChange={(e) => setAdj(r.key, { to: e.target.value })} />
                                </label>
                              </>
                            ) : null}
                            {needsReplacement(r.adj.action) ? (
                              <label className="mp-edit__wide">
                                Replaced by
                                <select value={r.adj.replacement} onChange={(e) => setAdj(r.key, { replacement: e.target.value })}>
                                  <option value="">Choose…</option>
                                  {users
                                    .filter((u) => String(u.id) !== r.uid)
                                    .sort((a, b) => Number(busy.has(String(a.id))) - Number(busy.has(String(b.id))) || nameOf(a).localeCompare(nameOf(b)))
                                    .map((u) => (
                                      <option key={u.id} value={String(u.id)}>
                                        {nameOf(u)} ({getRoleLabel(u)}){busy.has(String(u.id)) ? ' · busy' : ''}
                                        {onSchedule.has(String(u.id)) ? ' · already on this schedule' : ''}
                                      </option>
                                    ))}
                                </select>
                              </label>
                            ) : null}
                            <label className="mp-edit__wide">
                              Note
                              <input type="text" value={r.adj.notes} maxLength={300} placeholder="Optional, e.g. on leave" onChange={(e) => setAdj(r.key, { notes: e.target.value })} />
                            </label>
                          </div>
                        </td>
                      </tr>
                    ) : null,
                  ];
                })}
              </tbody>
            </table>
            {!rows.length ? <p className="mp-empty">No one on this schedule yet. Add people or a whole team from the left.</p> : null}
            {rows.length && !rosterShown.length ? <p className="mp-empty">No one matches “{rosterSearch}”.</p> : null}
          </div>
          <Pager paged={rosterPaged} noun="rows" compact />
        </section>
      </div>

      {!readOnly ? (
        <footer className="mp__foot">
          <span className="mp__foot-note">
            {dirty ? (
              <>
                <b>Unsaved changes</b>
                {problems.length ? ` · ${problems.length} adjustment${problems.length === 1 ? '' : 's'} to finish` : ''}
              </>
            ) : (
              'All changes saved'
            )}
          </span>
          <button type="button" className="mp-btn mp-btn--ghost" onClick={() => setDraft(initial)} disabled={!dirty || saving}>
            Discard changes
          </button>
          <button type="button" className="mp-btn mp-btn--primary" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save team'}
          </button>
        </footer>
      ) : null}
    </div>
  );
}

export default ManpowerEditor;
